import { ROUTES } from '../constants/routes';
import { UserSessionResponse } from '../services/ocpp/types';

/** The station/connector pair a live-session screen is addressed by. */
export interface SessionLocation {
  stationId: string;
  connectorId: string;
}

/**
 * A session that has a screen to open — a charge whose station record is gone
 * cannot be navigated to, so the two are worth telling apart in the type.
 */
export type OpenableSession = UserSessionResponse & {
  stationId: string;
  connectorId: string;
};

/** Where the charging tab should take the driver, if anywhere. */
export type ChargingTabTarget =
  | { kind: 'none' }
  | { kind: 'link'; path: string }
  | { kind: 'picker'; sessions: OpenableSession[] };

/** The live-session screen for one connector. */
export const stationSessionPath = (stationId: string, connectorId: string): string =>
  `/${ROUTES.CABINET}/${ROUTES.CAB_STATION}/${stationId}/${connectorId}`;

/**
 * The charge running at a given station. An account can hold several at once,
 * so a screen about one station must name it rather than take the first one.
 */
export const sessionForStation = (
  sessions: UserSessionResponse[],
  stationId: string | undefined,
): UserSessionResponse | undefined =>
  stationId ? sessions.find((session) => session.stationId === stationId) : undefined;

/**
 * The charge running on one connector. A station screen is addressed by
 * station *and* connector, so a charge on connector 1 must not make
 * connector 2's screen look busy — that is what stopped a driver from
 * starting a second charge on the other connector of the same station.
 */
export const sessionForConnector = (
  sessions: UserSessionResponse[],
  stationId: string | undefined,
  connectorId: string | number | undefined,
): UserSessionResponse | undefined =>
  stationId && connectorId !== undefined && connectorId !== ''
    ? sessions.find(
        (session) =>
          session.stationId === stationId &&
          Number(session.connectorId) === Number(connectorId),
      )
    : undefined;

/** Sessions that can actually be opened — a deleted station has no screen. */
const openable = (sessions: UserSessionResponse[]): OpenableSession[] =>
  sessions.filter(
    (session): session is OpenableSession => !!session.stationId && !!session.connectorId,
  );

/**
 * One live charge goes straight to its screen; several offer a choice. With
 * none, the idle (plugged-in but finished) session keeps the tab reachable.
 */
export const chargingTabTarget = (
  sessions: UserSessionResponse[],
  idle: SessionLocation | null,
): ChargingTabTarget => {
  const live = openable(sessions);

  if (live.length > 1) {
    return { kind: 'picker', sessions: live };
  }
  if (live.length === 1) {
    return {
      kind: 'link',
      path: stationSessionPath(live[0].stationId, live[0].connectorId),
    };
  }
  if (idle) {
    return { kind: 'link', path: stationSessionPath(idle.stationId, idle.connectorId) };
  }
  return { kind: 'none' };
};

/**
 * What the wallet is holding for charges in flight, in the balance's own unit.
 *
 * The estimate for each charge is deducted upfront, so the hold is the balance
 * as it stood before the earliest live charge minus what is left now. Reading
 * it off the newest charge instead would miss every earlier deduction, because
 * that session's `startedBalance` was already net of them.
 */
export const heldForCharging = (
  sessions: UserSessionResponse[],
  userBalance: number,
): number => {
  const balances = sessions
    .map((session) => session.startedBalance)
    .filter((balance): balance is number => typeof balance === 'number');

  if (!balances.length) return 0;

  return Math.max(0, Math.max(...balances) / 100 - userBalance);
};
